import { Box, Divider, Flex, HStack, VStack } from "@chakra-ui/react";
import { format } from "date-fns";
import CDS from "design-system";
import { ShowRichText } from "components/FormElements/Fields/RichTextAreaField";
import { ShowName } from "features/Resources/Shared/Components/Fields/NameField";
import TaskActionOutcome from "../Components/Fields/TaskActionOutcome";
import TagByResourceType from "components/Tag/TagByResourceType";
import { parseDateOnly } from "helpers/time";
import { Task } from "../models";
import { TASK_STATUSES } from "../constants";
import { formatTaskStartDate, showSkipButton, showMarkCompleteButton } from "../helpers";
import MarkSkippedToggleButton from "../Components/Button/MarkSkippedToggleButton";
import MarkCompleteToggleButton from "../Components/Button/MarkCompleteToggleButton";
import ResourceOptionMenu from "features/Resources/Shared/Components/Button/ResourceOptionMenu";

interface ClosedTaskProps {
  task: Task;
  timezone: string;
  includeHeader?: boolean;
  includeStatus?: boolean;
}

export default function ClosedTask({ task, timezone, includeHeader = true, includeStatus = true }: ClosedTaskProps) {
  const startDisplay = formatTaskStartDate(task, timezone);

  return (
    <VStack spacing={1} align="stretch" width="100%">
      {includeHeader && (
        <HStack justify="space-between" align="center" mb={2}>
          <HStack spacing={2} align="center" flex={1} minW={0}>
            <TagByResourceType type="task" />
            <ShowName name={task?.name} />
          </HStack>
          {task && (
            <HStack spacing={2} flexShrink={0}>
              {showSkipButton(task) && <MarkSkippedToggleButton task={task} />}
              {showMarkCompleteButton(task) && <MarkCompleteToggleButton task={task} />}
              <ResourceOptionMenu prefixId={task.prefixId} includeTemplate />
            </HStack>
          )}
        </HStack>
      )}

      <Flex gap={2} flexWrap="wrap" align="center" mb={2}>
        {includeStatus && (
          <CDS.BasePill cursor="default">
            <Flex direction="column" gap="0">
              <CDS.Text10 color="content.tertiary" fontWeight="500" lineHeight="1.3">
                Status
              </CDS.Text10>
              <HStack spacing="1" align="center">
                <Box
                  w="6px"
                  h="6px"
                  borderRadius="full"
                  bg={TASK_STATUSES.get(task.status)?.colors ?? "content.primary"}
                  flexShrink={0}
                />
                <CDS.Text12 fontWeight="700" color="content.primary" lineHeight="1.3">
                  {TASK_STATUSES.get(task.status)?.label ?? ""}
                </CDS.Text12>
              </HStack>
            </Flex>
          </CDS.BasePill>
        )}

        <CDS.AvatarAttributeTag
          label="Assignee"
          name={task.assignee?.fullName ?? "Unassigned"}
          avatarUrl={task.assignee?.avatarUrl ?? undefined}
          opacity={0.5}
          cursor="default"
        />

        {task.startAfterDependencyId ? (
          <CDS.AttributeTag label="Starts After" opacity={0.5} cursor="default">
            Dependency
          </CDS.AttributeTag>
        ) : startDisplay ? (
          <CDS.AttributeTag label="Starts" opacity={0.5} cursor="default">
            {startDisplay}
          </CDS.AttributeTag>
        ) : null}

        {task.dueOn && (
          <CDS.AttributeTag label="Due" opacity={0.5} cursor="default">
            {format(parseDateOnly(String(task.dueOn).slice(0, 10)), "MMM d, yyyy")}
          </CDS.AttributeTag>
        )}
      </Flex>

      <Divider my="4" />
      <TaskActionOutcome task={task} />
      <ShowRichText label="body" richText={task.descriptionDisplay ?? task.description ?? ""} />
    </VStack>
  );
}
